import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MessageSquare, Bell, Filter, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const KIND_COLOR = {
  maintenance: "text-amber-400", compliance: "text-rose-400", eta: "text-cyan-400",
  invite: "text-emerald-400", magic_link: "text-slate-400", portal_invite: "text-emerald-400",
  notification: "text-slate-400",
};

export default function Notifications() {
  const qc = useQueryClient();
  const [kind, setKind] = useState("all");
  const [channel, setChannel] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data });

  const markAll = async () => { await api.post("/notifications/mark-read"); toast.success("All marked read"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const deleteOne = async (id) => { await api.delete(`/notifications/${id}`); qc.invalidateQueries({ queryKey: ["notifications"] }); toast.success("Alert deleted"); };
  const clearAll = async () => { const { data } = await api.delete("/notifications"); qc.invalidateQueries({ queryKey: ["notifications"] }); toast.success(`Cleared ${data.deleted} alerts`); };

  const kinds = ["all", ...Array.from(new Set((data || []).map((n) => n.kind)))];
  const filtered = (data || []).filter((n) => (kind === "all" || n.kind === kind) && (channel === "all" || n.channel === channel));

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>In-app inbox</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Notification center</h1></div>
        <div className="flex gap-2">
          <button data-testid="mark-all-read-btn" onClick={markAll} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm text-white hover:border-emerald-500"><CheckCheck className="h-4 w-4" /> Mark all read</button>
          <button data-testid="clear-all-btn" onClick={clearAll} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm text-white hover:border-rose-500"><Trash2 className="h-4 w-4 text-rose-400" /> Clear all</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <select data-testid="filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none">
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select data-testid="filter-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none">
          {["all", "email", "sms"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-6 space-y-2">
          {filtered.map((n) => (
            <div key={n.id} data-testid={`notif-${n.id}`} className={`card-tac flex items-start gap-3 p-4 ${n.read ? "opacity-60" : ""}`}>
              <div className="mt-0.5">{n.channel === "sms" ? <MessageSquare className="h-4 w-4 text-cyan-400" /> : <Mail className="h-4 w-4 text-amber-400" />}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-mono-tac text-[10px] uppercase tracking-widest ${KIND_COLOR[n.kind] || "text-slate-400"}`}>{n.kind}</span>
                  {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                </div>
                <p className="mt-1 text-sm text-slate-200">{n.body}</p>
                <p className="mt-1 font-mono-tac text-[10px] text-slate-600">to {n.to} · {(n.created_at || "").slice(0, 19)}</p>
              </div>
              <button data-testid={`notif-delete-${n.id}`} onClick={() => deleteOne(n.id)} className="mt-0.5 text-slate-600 hover:text-rose-400" title="Delete alert"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!filtered.length && <div className="card-tac py-16 text-center text-slate-500" data-testid="notif-empty">No notifications match these filters.</div>}
        </div>
      )}
    </div>
  );
}
